/* eslint-disable no-unused-vars */
// chibi-rig.js — the animated vector TWIN of the explorer's sticker-art crab.
//
// Framework-free ES module (zero imports, zero assets): a faithful SVG trace
// of public/assets/explorer-chibi/content.png (same sticker halo, violet
// linework, coral shell, notched pincers) rebuilt in layers so the SAME
// character can genuinely animate — real eyelid blinks, pupil drift, claw
// pose transitions, mood-entrance hops/droops, ambient particles, idle bits.
// All 26 moods from the sprite-era table are composed from layers, so they
// stay consistent by construction. The rail card keeps rendering the
// original PNGs (user preference); this rig is for surfaces that need the
// crab ALIVE — first target: the live viewer's chibi bubble.
//
// Consumed by:
//   • React — dev-only /chibi-preview (art review); rail card only if/when
//     the user asks for the animated twin there.
//   • viewer.html — same file, copied into bots/coreclaw-backend/openclaw/
//     when the viewer port ships (Phase 2/3 ECR batch). Keep this file
//     dependency-free so that copy stays a plain <script type="module">.
//
// Public API:
//   const rig = createChibiRig(container, { mood, size, onMoodChange })
//   rig.setMood('excited')     — swaps layers + plays the transition
//   rig.lookAt(dx, dy)         — pupils flick toward a normalized direction
//                                (dx, dy ∈ [-1,1]); holds ~1.8s then drifts
//                                back (live viewer: crab watches his clicks)
//   rig.triggerIdleBit()       — force a random idle one-shot (mostly for QA)
//   rig.setGear('helmet')      — put on a space-suit piece with a drop-in
//                                animation ('gloves' | 'pack' | 'helmet');
//                                setGear(name, false) takes it off
//   rig.clearGear()            — remove every gear piece
//   rig.celebrate()            — one-shot confetti/star burst (liftoff!)
//   rig.destroy()              — cancel timers/animations, empty container
//   ALL_MOODS                  — every mood name (26)
//   MOOD_EMOJI                 — emoji per mood (degrade path for consumers)

// ── palette ──────────────────────────────────────────────────────────────────
// Traced from the beloved sticker-art PNG set (public/assets/explorer-chibi):
// coral crab, glossy top band, deep-violet outlines, lavender accents,
// dark-plum face ink, white sticker halo. The vector twin must read as the
// SAME character as content.png — sample there before changing any of these.
const BODY = '#f9928f';        // main shell coral
const BODY_LIGHT = '#ffc9bb';  // glossy top highlight band
const BODY_DARK = '#e0798f';   // lower shading (warm mauve)
const OUTLINE = '#5b3a8e';     // deep violet sticker linework
const INK = '#4a2c5e';         // eyes/mouth ink (dark plum)
const BLUSH = '#fb7ba6';
const TONGUE = '#ff9db4';
const ACCENT = '#c9a6f0';      // lavender (leg tips, joints, props)
const STICKER = '#ffffff';     // die-cut sticker halo
const STAR_GOLD = '#ffd76e';
const HEART_PINK = '#ff5f8f';
const TEAR_BLUE = '#9fd8ff';

// ── eye sets ─────────────────────────────────────────────────────────────────
// Each variant is a function (side) → SVG string drawn around (0,0); side is
// +1 for the left eye, -1 for the right (asymmetric sets mirror or differ).
// Structure contract: [data-lid] wraps anything the blink squashes;
// [data-pupil] wraps anything pupil-drift may nudge. Sets without pupils
// (crescents, hearts…) simply omit the hooks and skip those behaviors.
const SPARKLE_PATH = 'M0,-5 L1.3,-1.3 L5,0 L1.3,1.3 L0,5 L-1.3,1.3 L-5,0 L-1.3,-1.3 Z';
const HEART_PATH = 'M0,4.6 C-1.4,1.8 -6.4,0.4 -6.4,-3 C-6.4,-5.8 -3.8,-7.2 -1.9,-6.1 C-0.9,-5.6 0,-4.5 0,-4.5 C0,-4.5 0.9,-5.6 1.9,-6.1 C3.8,-7.2 6.4,-5.8 6.4,-3 C6.4,0.4 1.4,1.8 0,4.6 Z';
const STAR5_PATH = 'M0,-7 L1.9,-2.2 L7,-2.2 L2.9,0.9 L4.4,5.8 L0,2.9 L-4.4,5.8 L-2.9,0.9 L-7,-2.2 L-1.9,-2.2 Z';

const EYES = {
  // Big round anime eyes with soft highlights — the default "alive" look.
  roundSparkle: () => `
    <g data-lid>
      <circle r="12" fill="${INK}"/>
      <g data-pupil>
        <circle cx="-4" cy="-4.5" r="4.4" fill="#fff"/>
        <circle cx="4.6" cy="3.6" r="1.9" fill="#fff" opacity="0.9"/>
      </g>
    </g>`,
  // Even bigger, star-flare highlights — excited/aha energy.
  bigSparkle: () => `
    <g data-lid>
      <circle r="15.5" fill="${INK}"/>
      <g data-pupil>
        <path d="${SPARKLE_PATH}" transform="translate(-4.5,-5) scale(1.5)" fill="#fff"/>
        <circle cx="6" cy="5" r="2.6" fill="#fff" opacity="0.9"/>
        <circle cx="-7.5" cy="6" r="1.4" fill="#fff" opacity="0.8"/>
      </g>
    </g>`,
  // Closed happy ∩∩ crescents.
  crescent: () => `
    <path d="M-10,3.5 Q0,-8.5 10,3.5" stroke="${INK}" stroke-width="3.8"
      fill="none" stroke-linecap="round"/>`,
  // Sleepy ∪∪ curves.
  closedU: () => `
    <path d="M-9,-2 Q0,6.5 9,-2" stroke="${INK}" stroke-width="3.6"
      fill="none" stroke-linecap="round"/>`,
  // Half-lidded — bored/sly base (lid drawn in body color over the eyeball).
  halfLid: () => `
    <g data-lid>
      <circle r="11" fill="${INK}"/>
      <g data-pupil><circle cx="-3" cy="0.5" r="3.4" fill="#fff"/></g>
      <rect x="-12.5" y="-14" width="25" height="10.5" fill="${BODY}"/>
      <path d="M-11,-3.5 L11,-3.5" stroke="${OUTLINE}" stroke-width="3"
        stroke-linecap="round"/>
    </g>`,
  hearts: () => `
    <path d="${HEART_PATH}" transform="scale(1.7)" fill="${HEART_PINK}"
      stroke="#e04b7c" stroke-width="0.6"/>`,
  stars: () => `
    <path d="${STAR5_PATH}" transform="scale(1.8)" fill="${STAR_GOLD}"
      stroke="#e8a93d" stroke-width="0.7" stroke-linejoin="round"/>`,
  // >_< squeezed-shut giggle eyes (mirrors via side).
  squeeze: (side) => `
    <path d="M${-8 * side},-6 L${5 * side},0 L${-8 * side},6"
      stroke="${INK}" stroke-width="3.6" fill="none"
      stroke-linecap="round" stroke-linejoin="round"/>`,
  // Watery pleading 🥺 — round eye + welling sheen.
  watery: () => `
    <g data-lid>
      <circle r="12" fill="${INK}"/>
      <g data-pupil>
        <circle cx="-3.6" cy="-4" r="4" fill="#fff"/>
        <ellipse cx="0" cy="5" rx="6.5" ry="3.2" fill="#fff" opacity="0.75"/>
      </g>
    </g>`,
  // Wide shock — white eye, pin pupil.
  wideShock: () => `
    <g data-lid>
      <circle r="13" fill="#fff" stroke="${INK}" stroke-width="2.6"/>
      <g data-pupil><circle r="3.2" fill="${INK}"/></g>
    </g>`,
  // Cute-angry — smaller eye + inward brow line.
  angryV: (side) => `
    <path d="M${-12 * side},-13 L${5 * side},-7.5" stroke="${INK}"
      stroke-width="3.4" stroke-linecap="round"/>
    <g data-lid>
      <circle r="9.5" fill="${INK}"/>
      <g data-pupil><circle cx="${-3 * side}" cy="-3" r="3.2" fill="#fff"/></g>
    </g>`,
  // Detective asymmetry: left eye squints, right eye goes big.
  detective: (side) => (side === 1
    ? `<path d="M-9,0 L7,0" stroke="${INK}" stroke-width="4"
         stroke-linecap="round"/>
       <path d="M-9,-6 Q0,-9 8,-6" stroke="${INK}" stroke-width="2.6"
         fill="none" stroke-linecap="round"/>`
    : EYES.bigSparkle()),
  // Sly sideways half-lid with a raised outer brow.
  sly: (side) => `
    <path d="M${-12 * side},-12 Q0,-15 ${11 * side},-10" stroke="${INK}"
      stroke-width="3" fill="none" stroke-linecap="round"/>
    <g data-lid>
      <circle r="10.5" fill="${INK}"/>
      <g data-pupil><circle cx="${4 * side}" cy="0" r="3.2" fill="#fff"/></g>
      <rect x="-12" y="-13.5" width="24" height="9" fill="${BODY}"/>
      <path d="M-10.5,-4.5 L10.5,-4.5" stroke="${OUTLINE}" stroke-width="2.8"
        stroke-linecap="round"/>
    </g>`,
  // @_@ dizzy spirals — confused. Slow counter-rotation via SMIL so the
  // bewilderment reads even in a screenshot-sized bubble.
  swirl: (side) => `
    <g data-lid>
      <circle r="11.5" fill="#fff" stroke="${INK}" stroke-width="2.4"/>
      <path d="M0,0 m-7,0 a7,7 0 1,1 14,0 a5,5 0 1,1 -10,0 a3,3 0 1,1 6,0"
        stroke="${INK}" stroke-width="2.6" fill="none" stroke-linecap="round">
        <animateTransform attributeName="transform" type="rotate"
          from="0" to="${side * 360}" dur="3.2s" repeatCount="indefinite"/>
      </path>
    </g>`,
};

// ── mouths ───────────────────────────────────────────────────────────────────
const MOUTHS = {
  // The canonical kawaii ε / :3 — kept prominent, it's the signature mouth.
  kat3: `<path d="M-8.5,-1 Q-4.25,5 0,-1 Q4.25,5 8.5,-1" stroke="${INK}"
    stroke-width="3.4" fill="none" stroke-linecap="round"/>`,
  openSmile: `<path d="M-10,-1 Q0,13.5 10,-1 Z" fill="${INK}"/>
    <ellipse cx="0" cy="5.6" rx="5.4" ry="3.6" fill="${TONGUE}"/>`,
  bigLaugh: `<path d="M-12,-1 Q0,17 12,-1 Z" fill="${INK}"/>
    <ellipse cx="0" cy="7" rx="6.6" ry="4.4" fill="${TONGUE}"/>`,
  oMouth: `<ellipse rx="4.6" ry="6" fill="${INK}"/>
    <ellipse cx="0" cy="2.6" rx="2.6" ry="2" fill="${TONGUE}"/>`,
  oSmall: `<ellipse rx="3" ry="3.8" fill="${INK}"/>`,
  // Cute gritted-teeth zigzag — cartoon "grrr" without any real menace.
  grr: `<path d="M-7,1 L-3.5,-2 L0,1 L3.5,-2 L7,1" stroke="${INK}" stroke-width="3"
    fill="none" stroke-linecap="round" stroke-linejoin="round"/>`,
  flat: `<path d="M-6,1 L6,1" stroke="${INK}" stroke-width="3.2"
    stroke-linecap="round"/>`,
  pout: `<path d="M-6,3.5 Q0,-3 6,3.5" stroke="${INK}" stroke-width="3.2"
    fill="none" stroke-linecap="round"/>`,
  smirk: `<path d="M-6,2.5 Q1,6.5 8,-1.5" stroke="${INK}" stroke-width="3.2"
    fill="none" stroke-linecap="round"/>`,
  yawn: `<ellipse rx="6.5" ry="8.5" fill="${INK}"/>
    <ellipse cx="0" cy="4" rx="3.8" ry="3" fill="${TONGUE}"/>`,
  tinySmile: `<path d="M-4,0 Q0,3.5 4,0" stroke="${INK}" stroke-width="3"
    fill="none" stroke-linecap="round"/>`,
  hungrySmile: `<path d="M-8,-1 Q0,11 8,-1 Z" fill="${INK}"/>
    <ellipse cx="0" cy="4.6" rx="4.4" ry="3" fill="${TONGUE}"/>
    <path d="M8.5,1 q2.6,3.6 0,6.2 q-2.6,-2.6 0,-6.2" fill="${TEAR_BLUE}"/>`,
  // Trembling squiggle — confused / spooked / panicking.
  wavy: `<path d="M-8,0 Q-4,-3.5 0,0 Q4,3.5 8,0" stroke="${INK}"
    stroke-width="3.2" fill="none" stroke-linecap="round"/>`,
  // BLEH — disgusted tongue-out.
  tongueOut: `<path d="M-7,-2 Q0,4 7,-2 Z" fill="${INK}"/>
    <path d="M-3.5,0 Q-3.5,9 0,10 Q3.5,9 3.5,0 Z" fill="${TONGUE}"
      stroke="${OUTLINE}" stroke-width="1.6" stroke-linejoin="round"/>
    <path d="M0,2 L0,7.5" stroke="#e0798f" stroke-width="1.4"
      stroke-linecap="round"/>`,
};

// ── props (face-layer accessories) ──────────────────────────────────────────
// Drawn in body coordinates (200×200 viewBox). Emoji <text> is deliberate for
// the picture-props (bubble contents, bulb) — colorful and zero extra paths.
const PROPS = {
  glasses: `<g data-prop-bob="0">
    <circle cx="76" cy="104" r="16.5" fill="rgba(191,148,255,0.14)"
      stroke="${ACCENT}" stroke-width="3"/>
    <circle cx="124" cy="104" r="16.5" fill="rgba(191,148,255,0.14)"
      stroke="${ACCENT}" stroke-width="3"/>
    <path d="M92.5,102 Q100,97 107.5,102" stroke="${ACCENT}" stroke-width="3"
      fill="none"/>
    <circle cx="70" cy="98" r="2.2" fill="#fff" opacity="0.9"/>
  </g>`,
  magnifier: `<g data-prop-bob="1" transform="rotate(-18 132 122)">
    <circle cx="130" cy="116" r="13" fill="rgba(191,148,255,0.18)"
      stroke="${ACCENT}" stroke-width="3.4"/>
    <path d="M139,126 L147,136" stroke="${ACCENT}" stroke-width="5"
      stroke-linecap="round"/>
    <circle cx="126" cy="111" r="3" fill="#fff" opacity="0.85"/>
  </g>`,
  thoughtDots: `<g data-prop-bob="1">
    <circle cx="139" cy="62" r="3" fill="#fff" opacity="0.9"/>
    <circle cx="147" cy="50" r="4.4" fill="#fff" opacity="0.95"/>
    <ellipse cx="162" cy="32" rx="17" ry="13.5" fill="#fff" stroke="${OUTLINE}"
      stroke-width="2.6"/>
    <circle cx="155" cy="32" r="2.1" fill="${OUTLINE}"/>
    <circle cx="162" cy="32" r="2.1" fill="${OUTLINE}"/>
    <circle cx="169" cy="32" r="2.1" fill="${OUTLINE}"/>
  </g>`,
  thoughtSnack: `<g data-prop-bob="1">
    <circle cx="139" cy="62" r="3" fill="#fff" opacity="0.9"/>
    <circle cx="147" cy="50" r="4.4" fill="#fff" opacity="0.95"/>
    <ellipse cx="162" cy="32" rx="17" ry="14" fill="#fff" stroke="${OUTLINE}"
      stroke-width="2.6"/>
    <text x="162" y="38" font-size="15" text-anchor="middle">🍜</text>
  </g>`,
  bulb: `<g data-prop-bob="1">
    <circle cx="100" cy="26" r="16" fill="#fff3c4" opacity="0.5">
      <animate attributeName="opacity" values="0.5;0.85;0.5" dur="1.6s"
        repeatCount="indefinite"/>
    </circle>
    <text x="100" y="34" font-size="22" text-anchor="middle">💡</text>
  </g>`,
  question: `<g data-prop-bob="1">
    <text x="140" y="52" font-size="32" text-anchor="middle" fill="${OUTLINE}"
      font-weight="bold" font-family="monospace" stroke="#fff"
      stroke-width="1">?</text>
  </g>`,
  bangs: `<g data-prop-bob="1">
    <text x="64" y="42" font-size="22" text-anchor="middle" fill="#ff7d9c"
      font-weight="bold" font-family="monospace">!</text>
    <text x="138" y="36" font-size="26" text-anchor="middle" fill="#ff7d9c"
      font-weight="bold" font-family="monospace">!</text>
  </g>`,
  halo: `<g data-prop-bob="1">
    <ellipse cx="100" cy="30" rx="24" ry="7" fill="none" stroke="${STAR_GOLD}"
      stroke-width="4" opacity="0.9"/>
  </g>`,
  // Double question marks — confused (curious keeps the single "?").
  questions2: `<g data-prop-bob="1">
    <text x="62" y="48" font-size="26" text-anchor="middle" fill="${OUTLINE}"
      font-weight="bold" font-family="monospace" stroke="#fff"
      stroke-width="1" transform="rotate(-14 62 48)">?</text>
    <text x="142" y="42" font-size="32" text-anchor="middle" fill="${OUTLINE}"
      font-weight="bold" font-family="monospace" stroke="#fff"
      stroke-width="1" transform="rotate(12 142 42)">?</text>
  </g>`,
};

// Pose transforms are tuned against the traced anchors (shoulders at the
// shell's top corners). Poses in FRONT_CLAW_POSES additionally re-order the
// claw groups in front of the shell (applyMood) so they land on the belly /
// face instead of hiding behind the body like the resting poses do.
const CLAW_POSES = {
  up: { l: 'rotate(-4deg)', r: 'rotate(-4deg)' },
  raisedHigh: { l: 'translate(-4px,-14px) rotate(-26deg)', r: 'translate(-4px,-14px) rotate(-26deg)' },
  limp: { l: 'translate(-10px,32px) rotate(68deg)', r: 'translate(-10px,32px) rotate(68deg)' },
  // Both pincers together low on the belly — self-hug / namaste.
  clasped: { l: 'translate(22px,76px) rotate(30deg)', r: 'translate(22px,76px) rotate(30deg)' },
  // Right claw up at the chin, left resting — curious/thinking/shy.
  chin: { l: 'translate(-10px,32px) rotate(68deg)', r: 'translate(12px,56px) rotate(-10deg)' },
  // Hands-on-cheeks 😳 — pincers frame the face at blush level so the
  // squeezed eyes still peek out over them.
  cover: { l: 'translate(10px,56px) rotate(-8deg)', r: 'translate(10px,56px) rotate(-8deg)' },
  // Back of claw to forehead, other arm limp — the theatrical swoon.
  swoon: { l: 'translate(-10px,32px) rotate(68deg)', r: 'translate(2px,38px) rotate(-30deg)' },
};
const FRONT_CLAW_POSES = new Set(['clasped', 'chin', 'cover', 'swoon']);

// ── gear (space-suit pieces for the boot-cover suit-up scene) ────────────────
// Each piece is an SVG fragment slotted into a specific rig layer so it moves
// WITH the character: helmet rides data-body-tilt (tilts with the body, draws
// over the face), gloves live inside each claw's pose group (follow pose
// transitions), the pack tucks behind everything as data-sticker's first
// child (peeks above the shell + past its sides). Drawn in the same traced
// coordinate space as the body; glass is translucent so the face layers stay
// fully readable through it.
const GEAR = {
  helmet: `<g data-gear="helmet">
    <line x1="100" y1="57" x2="100" y2="38" stroke="${OUTLINE}"
      stroke-width="3" stroke-linecap="round"/>
    <circle cx="100" cy="33" r="4.5" fill="${HEART_PINK}" stroke="${OUTLINE}"
      stroke-width="2">
      <animate attributeName="opacity" values="1;0.35;1" dur="1.2s"
        repeatCount="indefinite"/>
    </circle>
    <circle cx="100" cy="122" r="66" fill="rgba(190,225,255,0.14)"
      stroke="rgba(232,243,255,0.9)" stroke-width="3"/>
    <path d="M56,94 Q68,66 96,57" stroke="#fff" stroke-width="5"
      stroke-linecap="round" fill="none" opacity="0.7"/>
    <path d="M64,108 Q72,93 86,85" stroke="#fff" stroke-width="3"
      stroke-linecap="round" fill="none" opacity="0.45"/>
    <rect x="76" y="180" width="48" height="10" rx="5" fill="${ACCENT}"
      stroke="${OUTLINE}" stroke-width="2.5"/>
  </g>`,
  // One glove, drawn in the same local frame as the claw art (the caller
  // appends it inside each pose group next to the scale(1.3) claw wrapper):
  // a white mitt redrawn over the pincer + a lavender cuff band.
  glove: `<g data-gear="glove" transform="scale(1.3)">
    <g transform="rotate(-20) translate(0,6)">
      <path d="M-7,-28.1 A14,14 0 1,0 7,-28.1 L0,-18.5 Z" fill="#ffffff"
        stroke="${OUTLINE}" stroke-width="3.6" stroke-linejoin="round"
        transform="scale(1.06)"/>
      <path d="M-8.5,-23 Q-11,-17 -8.5,-12" stroke="#e8f1ff" stroke-width="3.5"
        stroke-linecap="round" fill="none" opacity="0.9"/>
      <path d="M-10,-4.5 L10,-4.5" stroke="${ACCENT}" stroke-width="4.5"
        stroke-linecap="round"/>
    </g>
  </g>`,
  pack: `<g data-gear="pack">
    <g transform="translate(100,0)">
      <rect x="-64" y="74" width="22" height="52" rx="10" fill="#e7ebfa"
        stroke="${OUTLINE}" stroke-width="3.5"/>
      <rect x="42" y="74" width="22" height="52" rx="10" fill="#e7ebfa"
        stroke="${OUTLINE}" stroke-width="3.5"/>
      <rect x="-61" y="69" width="16" height="9" rx="4" fill="${ACCENT}"
        stroke="${OUTLINE}" stroke-width="2.5"/>
      <rect x="45" y="69" width="16" height="9" rx="4" fill="${ACCENT}"
        stroke="${OUTLINE}" stroke-width="2.5"/>
      <circle cx="-53" cy="88" r="3.2" fill="${STAR_GOLD}"/>
      <circle cx="53" cy="88" r="3.2" fill="${STAR_GOLD}"/>
    </g>
  </g>`,
  // ── watch-along pieces (CHIBI_WATCH_ALONG_SPEC §7) ──────────────────────
  // scarf: two-tone fan scarf wrapped low on the shell (below the mouth at
  // y143) with a fringed tail. Neutral green/white — chibi backs no club.
  scarf: `<g data-gear="scarf">
    <rect x="52" y="164" width="96" height="14" rx="7" fill="#3f9e5f"
      stroke="${OUTLINE}" stroke-width="3"/>
    <rect x="70" y="165.5" width="12" height="11" fill="#ffffff"/>
    <rect x="94" y="165.5" width="12" height="11" fill="#ffffff"/>
    <rect x="118" y="165.5" width="12" height="11" fill="#ffffff"/>
    <rect x="52" y="164" width="96" height="14" rx="7" fill="none"
      stroke="${OUTLINE}" stroke-width="3"/>
    <rect x="128" y="174" width="18" height="26" rx="4" fill="#3f9e5f"
      stroke="${OUTLINE}" stroke-width="3"/>
    <rect x="129.5" y="182" width="15" height="7" fill="#ffffff"/>
    <line x1="131" y1="200" x2="131" y2="206" stroke="${OUTLINE}"
      stroke-width="2.5" stroke-linecap="round"/>
    <line x1="137" y1="200" x2="137" y2="207" stroke="${OUTLINE}"
      stroke-width="2.5" stroke-linecap="round"/>
    <line x1="143" y1="200" x2="143" y2="206" stroke="${OUTLINE}"
      stroke-width="2.5" stroke-linecap="round"/>
  </g>`,
  // popcorn: striped bucket tucked front-left (clear of the mouth), puffs
  // peeking over the rim.
  popcorn: `<g data-gear="popcorn">
    <circle cx="58" cy="148" r="6" fill="#fff8e3" stroke="${OUTLINE}" stroke-width="2.5"/>
    <circle cx="68" cy="144" r="7" fill="#fff8e3" stroke="${OUTLINE}" stroke-width="2.5"/>
    <circle cx="78" cy="147" r="6" fill="#fff8e3" stroke="${OUTLINE}" stroke-width="2.5"/>
    <circle cx="63" cy="152" r="5.5" fill="#ffeeb8" stroke="${OUTLINE}" stroke-width="2.5"/>
    <circle cx="73" cy="152" r="5.5" fill="#ffeeb8" stroke="${OUTLINE}" stroke-width="2.5"/>
    <path d="M50,154 L86,154 L82,192 L54,192 Z" fill="#ffffff"
      stroke="${OUTLINE}" stroke-width="3" stroke-linejoin="round"/>
    <path d="M54,154 L60,154 L58,192 L54.5,192 Z" fill="#e2554f"/>
    <path d="M66,154 L72,154 L71,192 L66.5,192 Z" fill="#e2554f"/>
    <path d="M78,154 L84,154 L81.5,192 L77,192 Z" fill="#e2554f"/>
    <rect x="48" y="150" width="40" height="7" rx="3.5" fill="#ffffff"
      stroke="${OUTLINE}" stroke-width="3"/>
  </g>`,
};

// Drop-in animations per piece — the helmet lands with a clunk (overshoot +
// settle, plus a body squash triggered by setGear), gloves pop, pack thunks.
const GEAR_ANIM = {
  helmet: { frames: [
    { transform: 'translateY(-46px)', opacity: 0 },
    { transform: 'translateY(3px)', opacity: 1, offset: 0.62 },
    { transform: 'translateY(-2px)', offset: 0.82 },
    { transform: 'translateY(0)', opacity: 1 }], ms: 460 },
  gloves: { frames: [
    { transform: 'scale(0.2)', opacity: 0 },
    { transform: 'scale(1.18)', opacity: 1, offset: 0.65 },
    { transform: 'scale(1)', opacity: 1 }], ms: 340 },
  pack: { frames: [
    { transform: 'translateY(-30px)', opacity: 0 },
    { transform: 'translateY(2px)', opacity: 1, offset: 0.7 },
    { transform: 'translateY(0)', opacity: 1 }], ms: 380 },
  scarf: { frames: [
    { transform: 'translateY(-30px)', opacity: 0 },
    { transform: 'translateY(3px)', opacity: 1, offset: 0.68 },
    { transform: 'translateY(0)', opacity: 1 }], ms: 420 },
  popcorn: { frames: [
    { transform: 'translateY(26px)', opacity: 0 },
    { transform: 'translateY(-4px)', opacity: 1, offset: 0.66 },
    { transform: 'translateY(0)', opacity: 1 }], ms: 380 },
};

// ── particles ────────────────────────────────────────────────────────────────
// DOM spans in an overlay div; each config = which glyphs, spawn cadence,
// which CSS animation, and a cap so a busy mood can't flood the card.
const FX = {
  hearts: { glyphs: ['♡', '💕', '♡'], every: [900, 1600], anim: 'chibiFxFloat', max: 5, color: HEART_PINK, size: [11, 16] },
  heartsSoft: { glyphs: ['♡'], every: [1400, 2400], anim: 'chibiFxFloat', max: 3, color: '#ffb3cd', size: [10, 13] },
  sparkle: { glyphs: ['✦', '✧', '✨'], every: [700, 1400], anim: 'chibiFxTwinkle', max: 6, color: STAR_GOLD, size: [10, 15] },
  sparklePurple: { glyphs: ['✦', '✧'], every: [900, 1700], anim: 'chibiFxTwinkle', max: 4, color: ACCENT, size: [10, 14] },
  stars: { glyphs: ['⭐', '✨', '🌟'], every: [650, 1200], anim: 'chibiFxTwinkle', max: 7, size: [11, 16] },
  zzz: { glyphs: ['z', 'Z'], every: [1300, 2200], anim: 'chibiFxDrift', max: 3, color: '#b9c7ff', size: [12, 18], origin: [0.66, 0.28] },
  confetti: { glyphs: ['▪'], every: [220, 420], anim: 'chibiFxFall', max: 14, colors: ['#ff8fb3', '#bf94ff', '#ffd76e', '#8fd8ff', '#9dffc8'], size: [7, 11] },
  anger: { glyphs: ['💢'], every: [1500, 2600], anim: 'chibiFxPulse', max: 1, size: [14, 16], origin: [0.74, 0.22] },
  steam: { glyphs: ['💨'], every: [1600, 2600], anim: 'chibiFxDrift', max: 2, size: [12, 14], origin: [0.3, 0.24] },
  tear: { glyphs: ['💧'], every: [2200, 3600], anim: 'chibiFxTearSlide', max: 1, size: [11, 12], origin: [0.63, 0.55] },
  sweat: { glyphs: ['💧'], every: [2600, 4200], anim: 'chibiFxTearSlide', max: 1, size: [11, 12], origin: [0.24, 0.4] },
  // Single anime sweat drop on ONE temple — the classic "well, that stung"
  // beat (user ask 07-12: disappointed = drop of sweat on one side).
  sweatSide: { glyphs: ['💧'], every: [2200, 3600], anim: 'chibiFxTearSlide', max: 1, size: [12, 14], origin: [0.76, 0.3] },
  music: { glyphs: ['♪', '♫'], every: [700, 1300], anim: 'chibiFxFloat', max: 5, color: ACCENT, size: [12, 17] },
  sigh: { glyphs: ['💨'], every: [2800, 4200], anim: 'chibiFxDrift', max: 1, size: [11, 12], origin: [0.42, 0.62] },
  bubbles: { glyphs: ['🫧'], every: [1500, 2600], anim: 'chibiFxFloat', max: 3, size: [10, 14] },
  // Rapid multi-drop sweat — panicking (single-drop 'sweat' stays for
  // embarrassed).
  panicSweat: { glyphs: ['💧'], every: [500, 1000], anim: 'chibiFxTearSlide', max: 3, size: [10, 13], origin: [0.3, 0.35] },
};

// ── mood catalog ─────────────────────────────────────────────────────────────
// entrance: hop (bouncy arrival), droop (sag), flinch (startle), pop (default
// soft swap). loop: continuous body motion while the mood holds.
// look: resting pupil offset. All 26 moods from the sprite-era table, now
// actually wired.
const MOODS = {
  sleeping: { eyes: 'closedU', mouth: 'tinySmile', blush: 1, claws: 'clasped', fx: 'zzz', entrance: 'droop', breathe: 'slow' },
  bored: { eyes: 'halfLid', mouth: 'flat', blush: 1, claws: 'limp', entrance: 'droop', look: { x: -2, y: 1 } },
  content: { eyes: 'crescent', mouth: 'kat3', blush: 2, claws: 'up' },
  excited: { eyes: 'bigSparkle', mouth: 'openSmile', blush: 3, claws: 'raisedHigh', fx: 'sparkle', entrance: 'hop' },
  curious: { eyes: 'roundSparkle', mouth: 'oSmall', blush: 2, claws: 'chin', prop: 'question', look: { x: 3, y: -3 }, entrance: 'pop' },
  cheering: { eyes: 'hearts', mouth: 'bigLaugh', blush: 3, claws: 'raisedHigh', fx: 'confetti', entrance: 'hop', loop: 'bounce' },
  thinking: { eyes: 'roundSparkle', mouth: 'smirk', blush: 2, claws: 'chin', prop: 'thoughtDots', look: { x: 2.5, y: -4 } },
  focused: { eyes: 'halfLid', mouth: 'smirk', blush: 2, claws: 'chin', prop: 'glasses', fx: 'sparklePurple', look: { x: 0, y: 1 } },
  looking: { eyes: 'bigSparkle', mouth: 'oSmall', blush: 3, claws: 'up', fx: 'heartsSoft', loop: 'scan', entrance: 'pop' },
  searching: { eyes: 'detective', mouth: 'smirk', blush: 2, claws: 'chin', prop: 'magnifier', fx: 'sparklePurple' },
  surprised: { eyes: 'wideShock', mouth: 'oMouth', blush: 2, claws: 'raisedHigh', prop: 'bangs', entrance: 'flinch' },
  proud: { eyes: 'crescent', mouth: 'smirk', blush: 3, claws: 'up', prop: 'halo', fx: 'stars', entrance: 'hop', tilt: -3 },
  frustrated: { eyes: 'angryV', mouth: 'pout', blush: 2, claws: 'raisedHigh', fx: 'anger', loop: 'tremble' },
  sad: { eyes: 'watery', mouth: 'pout', blush: 2, claws: 'clasped', fx: 'tear', entrance: 'droop', tilt: 2 },
  // 07-12 user pass: disappointed = downcast half-lids + the classic single
  // side sweat drop (game lost / voted out beat, flashed by the live viewer).
  disappointed: { eyes: 'halfLid', mouth: 'flat', blush: 1, claws: 'limp', fx: 'sweatSide', entrance: 'droop', tilt: 3, look: { x: -1.5, y: 2 } },
  // 07-12 addition: cutesy-angry — puffed blush cheeks + grr zigzag + one 💢,
  // trembling in place. Distinct from frustrated (pout): this one is a full
  // tantrum, but adorable. No real menace by construction.
  angry: { eyes: 'angryV', mouth: 'grr', blush: 3, claws: 'raisedHigh', fx: 'anger', loop: 'tremble', entrance: 'flinch' },
  shy: { eyes: 'crescent', mouth: 'tinySmile', blush: 3, claws: 'chin', fx: 'heartsSoft', tilt: 3 },
  yawning: { eyes: 'closedU', mouth: 'yawn', blush: 2, claws: 'chin', fx: 'zzz', entrance: 'droop' },
  hungry: { eyes: 'bigSparkle', mouth: 'hungrySmile', blush: 3, claws: 'clasped', prop: 'thoughtSnack', look: { x: 4, y: -2 } },
  giggling: { eyes: 'squeeze', mouth: 'bigLaugh', blush: 3, claws: 'chin', fx: 'music', loop: 'giggle' },
  dreamy: { eyes: 'hearts', mouth: 'oSmall', blush: 3, claws: 'clasped', fx: 'hearts', loop: 'sway', tilt: -2 },
  mischievous: { eyes: 'sly', mouth: 'smirk', blush: 2, claws: 'clasped', fx: 'sparklePurple', look: { x: 4, y: 0 } },
  aha: { eyes: 'bigSparkle', mouth: 'oMouth', blush: 2, claws: 'raisedHigh', prop: 'bulb', fx: 'sparkle', entrance: 'hop', look: { x: 0, y: -5 } },
  dancing: { eyes: 'crescent', mouth: 'bigLaugh', blush: 3, claws: 'raisedHigh', fx: 'music', entrance: 'hop', loop: 'sway' },
  starstruck: { eyes: 'stars', mouth: 'oSmall', blush: 3, claws: 'clasped', fx: 'stars', entrance: 'hop' },
  grateful: { eyes: 'crescent', mouth: 'kat3', blush: 3, claws: 'clasped', fx: 'sparkle', tilt: 4 },
  embarrassed: { eyes: 'squeeze', mouth: 'tinySmile', blush: 3, claws: 'cover', fx: 'sweat', entrance: 'flinch', tilt: 2 },
  // ── 07-12 additions: journal `feeling` enum + comedy set ──────────────
  confused: { eyes: 'swirl', mouth: 'wavy', blush: 2, claws: 'chin', prop: 'questions2', loop: 'sway', tilt: 4, entrance: 'pop' },
  spooked: { eyes: 'wideShock', mouth: 'wavy', blush: 1, claws: 'cover', fx: 'sweat', entrance: 'flinch', loop: 'tremble' },
  disgusted: { eyes: 'squeeze', mouth: 'tongueOut', blush: 1, claws: 'limp', fx: 'sigh', entrance: 'flinch', tilt: -4 },
  panicking: { eyes: 'wideShock', mouth: 'wavy', blush: 2, claws: 'raisedHigh', fx: 'panicSweat', entrance: 'flinch', loop: 'tremble' },
  dramatic: { eyes: 'closedU', mouth: 'pout', blush: 2, claws: 'swoon', fx: 'sigh', entrance: 'droop', tilt: -6 },
};

const ALL_MOODS = Object.keys(MOODS);

// Emoji degrade path — consumers can render these if the rig can't mount
// (kept in the rig so the vocabulary lives in ONE file).
const MOOD_EMOJI = {
  sleeping: '😴', bored: '🥱', content: '😊', excited: '🤩', curious: '🤨',
  cheering: '🥳', thinking: '🤔', focused: '🤓', looking: '👀', searching: '🔎',
  surprised: '😲', proud: '😎', frustrated: '😤', sad: '🥺', disappointed: '😔', angry: '😠',
  shy: '☺️', yawning: '😪', hungry: '🤤', giggling: '😆', dreamy: '🥰',
  mischievous: '😏', aha: '💡', dancing: '💃', starstruck: '🌟',
  grateful: '🙏', embarrassed: '😳',
  confused: '😵‍💫', spooked: '😱', disgusted: '🤢', panicking: '😰',
  dramatic: '🎭',
};

// ── static body SVG ──────────────────────────────────────────────────────────
// Everything that never swaps: legs, shell, gloss, claw art, sticker halo.
// The mutable bits are empty groups the mood application fills in. Geometry
// and rendering order are traced from content.png: pincers peek from BEHIND
// the shell's top edge by default (applyMood re-orders them in front only
// for the clasped/cover/chin poses so those don't vanish behind the body).
function bodySvg() {
  // One pincer, drawn left-handed around its shoulder joint at (0,0):
  // a short arm + a round pincer with a V notch cut into its top edge
  // (circle r14 centered (0,-16); notch apex 6 units deep), plus lavender
  // joint + shine accents like the PNG's.
  const claw = `
    <path d="M0,4 L0,-4" stroke="${OUTLINE}" stroke-width="12"
      stroke-linecap="round"/>
    <path d="M0,4 L0,-3" stroke="${BODY}" stroke-width="6.5"
      stroke-linecap="round"/>
    <path d="M-3.5,-1 L3.5,-1" stroke="${ACCENT}" stroke-width="3"
      stroke-linecap="round" opacity="0.9"/>
    <g transform="rotate(-20) translate(0,6)">
      <path d="M-7,-28.1 A14,14 0 1,0 7,-28.1 L0,-18.5 Z" fill="${BODY}"
        stroke="${OUTLINE}" stroke-width="3.6" stroke-linejoin="round"/>
      <path d="M-9,-23 Q-11.5,-17 -8.5,-11" stroke="${BODY_LIGHT}"
        stroke-width="4" stroke-linecap="round" fill="none" opacity="0.9"/>
      <circle cx="6" cy="-7" r="3" fill="${ACCENT}" opacity="0.75"/>
    </g>`;
  // Three pointed little legs per side, lavender-tipped like the PNG.
  const legs = (sx) => `
    <g transform="translate(100,0) scale(${sx},1) translate(-100,0)">
      <g transform="translate(14,26) scale(0.72)">
      <path d="M60,148 Q48,160 42,172 Q52,168 64,158 Z" fill="${BODY}"
        stroke="${OUTLINE}" stroke-width="3" stroke-linejoin="round"/>
      <path d="M50,138 Q36,146 28,156 Q40,154 54,146 Z" fill="${BODY}"
        stroke="${OUTLINE}" stroke-width="3" stroke-linejoin="round"/>
      <path d="M44,126 Q30,130 22,138 Q34,138 48,133 Z" fill="${BODY}"
        stroke="${OUTLINE}" stroke-width="3" stroke-linejoin="round"/>
      <circle cx="43" cy="170" r="2.6" fill="${ACCENT}"/>
      <circle cx="29" cy="154" r="2.6" fill="${ACCENT}"/>
      <circle cx="23" cy="137" r="2.6" fill="${ACCENT}"/>
      </g>
    </g>`;
  return `
  <svg viewBox="0 0 200 200" width="100%" height="100%"
    style="display:block;overflow:visible">
    <defs>
      <filter id="chibiStickerHalo" x="-15%" y="-15%" width="130%" height="130%">
        <feMorphology in="SourceAlpha" operator="dilate" radius="5" result="dilated"/>
        <feFlood flood-color="${STICKER}" result="white"/>
        <feComposite in="white" in2="dilated" operator="in" result="halo"/>
        <feMerge>
          <feMergeNode in="halo"/>
          <feMergeNode in="SourceGraphic"/>
        </feMerge>
      </filter>
    </defs>
    <ellipse cx="100" cy="182" rx="38" ry="6" fill="#000" opacity="0.12"/>
    <g data-sticker filter="url(#chibiStickerHalo)">
      <g data-claw="l" transform="translate(59,92)">
        <g data-claw-pose><g transform="scale(1.3)">${claw}</g></g>
      </g>
      <g data-claw="r" transform="translate(141,92) scale(-1,1)">
        <g data-claw-pose><g transform="scale(1.3)">${claw}</g></g>
      </g>
      ${legs(1)}${legs(-1)}
      <g data-body-tilt>
        <ellipse cx="100" cy="126" rx="52" ry="39" fill="${BODY}"
          stroke="${OUTLINE}" stroke-width="4.5"/>
        <ellipse cx="100" cy="150" rx="38" ry="11" fill="${BODY_DARK}"
          opacity="0.5"/>
        <ellipse cx="100" cy="104" rx="38" ry="12.5" fill="${BODY_LIGHT}"
          opacity="0.8"/>
        <ellipse cx="72" cy="98" rx="10" ry="5" fill="#fff"
          opacity="0.85" transform="rotate(-14 72 98)"/>
        <g data-face>
          <ellipse data-blush="l" cx="59" cy="134" rx="10.5" ry="7"
            fill="${BLUSH}"/>
          <ellipse data-blush="r" cx="141" cy="134" rx="10.5" ry="7"
            fill="${BLUSH}"/>
          <g data-eye="l" transform="translate(79,117)"></g>
          <g data-eye="r" transform="translate(121,117)"></g>
          <g data-mouth transform="translate(100,143)"></g>
          <g data-prop></g>
        </g>
      </g>
    </g>
  </svg>`;
}

// ── shared stylesheet (one per document) ─────────────────────────────────────
const STYLE_ID = 'chibi-rig-style';
const STYLE_TEXT = `
.chibi-rig-root { position: relative; width: 100%; height: 100%; }
.chibi-rig-breathe { width: 100%; height: 100%;
  animation: chibiRigBreathe 3.6s ease-in-out infinite; }
.chibi-rig-breathe.slow { animation-duration: 5.6s; }
.chibi-rig-motion { width: 100%; height: 100%; }
.chibi-rig-fx { position: absolute; inset: 0; overflow: visible;
  pointer-events: none; z-index: 2; }
.chibi-rig-fx span { position: absolute; will-change: transform, opacity;
  text-shadow: 0 1px 2px rgba(0,0,0,0.18); }
@keyframes chibiRigBreathe {
  0%, 100% { transform: scale(1); } 50% { transform: scale(1.024); } }
@keyframes chibiFxFloat {
  0% { transform: translateY(4px) scale(0.6); opacity: 0; }
  18% { opacity: 1; }
  100% { transform: translateY(-46px) scale(1.15); opacity: 0; } }
@keyframes chibiFxTwinkle {
  0% { transform: scale(0.2) rotate(0deg); opacity: 0; }
  35% { transform: scale(1.25) rotate(18deg); opacity: 1; }
  100% { transform: scale(0.35) rotate(40deg); opacity: 0; } }
@keyframes chibiFxDrift {
  0% { transform: translate(0,4px) scale(0.7); opacity: 0; }
  20% { opacity: 0.95; }
  100% { transform: translate(16px,-34px) scale(1.1); opacity: 0; } }
@keyframes chibiFxFall {
  0% { transform: translateY(-6px) rotate(0deg); opacity: 1; }
  100% { transform: translateY(52px) rotate(220deg); opacity: 0; } }
@keyframes chibiFxPulse {
  0% { transform: scale(0.5); opacity: 0; }
  30% { transform: scale(1.2); opacity: 1; }
  70% { transform: scale(1); opacity: 1; }
  100% { transform: scale(0.8); opacity: 0; } }
@keyframes chibiFxTearSlide {
  0% { transform: translateY(0) scale(0.8); opacity: 0; }
  25% { opacity: 0.95; }
  100% { transform: translateY(16px) scale(1); opacity: 0; } }
@keyframes chibiPropBob {
  0%, 100% { transform: translateY(0); } 50% { transform: translateY(-3.5px); } }
`;

function ensureStyle(doc) {
  if (doc.getElementById(STYLE_ID)) return;
  const style = doc.createElement('style');
  style.id = STYLE_ID;
  style.textContent = STYLE_TEXT;
  doc.head.appendChild(style);
}

// ── WAAPI keyframe sets ──────────────────────────────────────────────────────
const ENTRANCES = {
  pop: [
    { transform: 'scale(0.94)', offset: 0 },
    { transform: 'scale(1.03)', offset: 0.6 },
    { transform: 'scale(1)', offset: 1 },
  ],
  hop: [
    { transform: 'translateY(0) scaleY(1) scaleX(1)', offset: 0 },
    { transform: 'translateY(2%) scaleY(0.88) scaleX(1.08)', offset: 0.2 },
    { transform: 'translateY(-11%) scaleY(1.06) scaleX(0.95)', offset: 0.55 },
    { transform: 'translateY(1%) scaleY(0.94) scaleX(1.04)', offset: 0.82 },
    { transform: 'translateY(0) scaleY(1) scaleX(1)', offset: 1 },
  ],
  droop: [
    { transform: 'translateY(-2%)', offset: 0 },
    { transform: 'translateY(2.5%)', offset: 0.65 },
    { transform: 'translateY(1.5%)', offset: 1 },
  ],
  flinch: [
    { transform: 'scale(1) rotate(0deg)', offset: 0 },
    { transform: 'scale(1.1) rotate(-3deg)', offset: 0.25 },
    { transform: 'scale(1.06) rotate(3deg)', offset: 0.5 },
    { transform: 'scale(1.08) rotate(-1.5deg)', offset: 0.72 },
    { transform: 'scale(1) rotate(0deg)', offset: 1 },
  ],
};
const ENTRANCE_MS = { pop: 300, hop: 620, droop: 700, flinch: 420 };

const LOOPS = {
  sway: { frames: [
    { transform: 'rotate(-5deg)' }, { transform: 'rotate(5deg)' },
    { transform: 'rotate(-5deg)' }], opts: { duration: 1500, iterations: Infinity, easing: 'ease-in-out' } },
  bounce: { frames: [
    { transform: 'translateY(0)' }, { transform: 'translateY(-4.5%)' },
    { transform: 'translateY(0)' }], opts: { duration: 640, iterations: Infinity, easing: 'ease-in-out' } },
  tremble: { frames: [
    { transform: 'translateX(-1.2%) rotate(-0.8deg)' },
    { transform: 'translateX(1.2%) rotate(0.8deg)' }],
    opts: { duration: 130, iterations: Infinity, direction: 'alternate' } },
  giggle: { frames: [
    { transform: 'rotate(-2.4deg) translateY(0)' },
    { transform: 'rotate(2.4deg) translateY(-1.5%)' }],
    opts: { duration: 240, iterations: Infinity, direction: 'alternate', easing: 'ease-in-out' } },
  // scan is pupil-driven, handled by the look scheduler, not a body loop.
  scan: null,
};

const BLUSH_OPACITY = { 1: 0.3, 2: 0.55, 3: 0.85 };

const rand = (min, max) => min + Math.random() * (max - min);
const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];

// ── the rig ──────────────────────────────────────────────────────────────────
export function createChibiRig(container, opts = {}) {
  const doc = container.ownerDocument;
  ensureStyle(doc);

  const root = doc.createElement('div');
  root.className = 'chibi-rig-root';
  root.setAttribute('data-chibi-rig', '');
  const fxLayer = doc.createElement('div');
  fxLayer.className = 'chibi-rig-fx';
  const breathe = doc.createElement('div');
  breathe.className = 'chibi-rig-breathe';
  const motion = doc.createElement('div');
  motion.className = 'chibi-rig-motion';
  motion.innerHTML = bodySvg();
  breathe.appendChild(motion);
  root.appendChild(breathe);
  root.appendChild(fxLayer);
  container.appendChild(root);

  const svg = motion.querySelector('svg');
  const q = (sel) => svg.querySelector(sel);
  const el = {
    eyeL: q('[data-eye="l"]'), eyeR: q('[data-eye="r"]'),
    mouth: q('[data-mouth]'), prop: q('[data-prop]'),
    blushL: q('[data-blush="l"]'), blushR: q('[data-blush="r"]'),
    clawL: q('[data-claw="l"] [data-claw-pose]'),
    clawR: q('[data-claw="r"] [data-claw-pose]'),
    clawOuterL: q('[data-claw="l"]'), clawOuterR: q('[data-claw="r"]'),
    face: q('[data-face]'), bodyTilt: q('[data-body-tilt]'),
    sticker: q('[data-sticker]'),
  };
  // SVG group CSS transforms need fill-box so origins mean "this element".
  [el.clawL, el.clawR, el.eyeL, el.eyeR, el.face, el.bodyTilt].forEach((g) => {
    g.style.transformBox = 'fill-box';
    g.style.transformOrigin = 'center';
  });
  el.clawL.style.transition = 'transform 0.38s cubic-bezier(0.34,1.4,0.5,1)';
  el.clawR.style.transition = 'transform 0.38s cubic-bezier(0.34,1.4,0.5,1)';
  el.bodyTilt.style.transition = 'transform 0.45s ease';

  let mood = null;
  let destroyed = false;
  const timers = new Set();
  let loopAnim = null;
  let entranceAnim = null;
  let fxTimer = null;
  let fxCount = 0;

  const later = (fn, ms) => {
    const id = setTimeout(() => { timers.delete(id); if (!destroyed) fn(); }, ms);
    timers.add(id);
    return id;
  };

  // ── particles ──────────────────────────────────────────────────────────
  function stopFx() {
    if (fxTimer) { clearTimeout(fxTimer); timers.delete(fxTimer); fxTimer = null; }
  }
  function scheduleFx(cfg) {
    stopFx();
    const tick = () => {
      fxTimer = later(() => { spawnFx(cfg); tick(); }, rand(cfg.every[0], cfg.every[1]));
    };
    tick();
  }
  function spawnFx(cfg, glyphOverride, originOverride) {
    if (fxCount >= (cfg.max || 5) || doc.hidden) return;
    const s = doc.createElement('span');
    s.textContent = glyphOverride || pick(cfg.glyphs);
    const origin = originOverride || cfg.origin;
    const ox = origin ? origin[0] : rand(0.15, 0.85);
    const oy = origin ? origin[1] : rand(0.35, 0.75);
    s.style.left = `${(ox + rand(-0.05, 0.05)) * 100}%`;
    s.style.top = `${(oy + rand(-0.04, 0.04)) * 100}%`;
    s.style.fontSize = `${rand(cfg.size[0], cfg.size[1])}px`;
    s.style.color = cfg.colors ? pick(cfg.colors) : (cfg.color || '#fff');
    const dur = cfg.anim === 'chibiFxFall' ? rand(1100, 1700) : rand(1300, 2100);
    s.style.animation = `${cfg.anim} ${dur}ms ease-out forwards`;
    fxCount += 1;
    s.addEventListener('animationend', () => { s.remove(); fxCount -= 1; }, { once: true });
    fxLayer.appendChild(s);
  }

  // ── blink + pupil look ─────────────────────────────────────────────────
  function blinkOnce() {
    svg.querySelectorAll('[data-lid]').forEach((lid) => {
      lid.style.transformBox = 'fill-box';
      lid.style.transformOrigin = 'center';
      lid.animate(
        [{ transform: 'scaleY(1)' }, { transform: 'scaleY(0.07)' }, { transform: 'scaleY(1)' }],
        { duration: 150, easing: 'ease-in-out' },
      );
    });
  }
  function scheduleBlink() {
    later(() => {
      blinkOnce();
      // Occasional double-blink — small thing, big life.
      if (Math.random() < 0.22) later(blinkOnce, 240);
      scheduleBlink();
    }, rand(2400, 6200));
  }

  function setLook(x, y, transitionMs = 420) {
    svg.querySelectorAll('[data-pupil]').forEach((p) => {
      p.style.transition = `transform ${transitionMs}ms ease`;
      p.style.transform = `translate(${x}px, ${y}px)`;
    });
  }
  // Public gaze (07-12): point the pupils toward a normalized direction
  // (dx, dy ∈ [-1, 1]; 0,0 = straight ahead). The live viewer feeds it the
  // bot's click coordinates so the bubble chibi visibly WATCHES his own
  // cursor work. Holds against the ambient look scheduler for holdMs, then
  // drifts back to the mood's resting look on the next scheduler tick.
  let gazeHoldUntil = 0;
  function lookAt(dx, dy, holdMs = 1800) {
    const nx = Math.max(-1, Math.min(1, Number(dx) || 0));
    const ny = Math.max(-1, Math.min(1, Number(dy) || 0));
    gazeHoldUntil = Date.now() + holdMs;
    setLook(nx * 5.5, ny * 3.5, 200);
  }
  function scheduleLook() {
    later(() => {
      // An externally-directed gaze (lookAt) is holding — don't stomp it;
      // check back on the next tick.
      if (Date.now() < gazeHoldUntil) { scheduleLook(); return; }
      const cfg = MOODS[mood] || {};
      const base = cfg.look || { x: 0, y: 0 };
      if (cfg.loop === 'scan') {
        // Looking-around mood: deliberate left…right sweeps.
        setLook(base.x - 5, base.y, 600);
        later(() => setLook(base.x + 5, base.y, 700), 900);
      } else if (Math.random() < 0.55) {
        setLook(base.x + rand(-2, 2), base.y + rand(-1.5, 1.5));
      } else {
        setLook(base.x, base.y);
      }
      scheduleLook();
    }, rand(1800, 4200));
  }

  // ── idle one-shots (clip bait) ─────────────────────────────────────────
  const IDLE_SAFE = new Set(['content', 'bored', 'looking', 'curious', 'thinking', 'focused']);
  const IDLE_BITS = ['wobble', 'sideEye', 'wave', 'bubble', 'hop'];
  function playIdleBit(name) {
    const bit = name || (Math.random() < 0.55 ? 'wobble'
      : Math.random() < 0.5 ? 'sideEye'
        : Math.random() < 0.6 ? 'wave'
          : Math.random() < 0.65 ? 'bubble' : 'hop');
    if (bit === 'wobble') {
      motion.animate([
        { transform: 'rotate(0deg)' }, { transform: 'rotate(-3.5deg)' },
        { transform: 'rotate(0deg)' }, { transform: 'rotate(3.5deg)' },
        { transform: 'rotate(0deg)' }],
      { duration: 700, easing: 'ease-in-out' });
    } else if (bit === 'sideEye') {
      const back = (MOODS[mood] || {}).look || { x: 0, y: 0 };
      setLook(5.5, 0.5, 180);
      later(() => setLook(back.x, back.y, 300), 900);
    } else if (bit === 'wave') {
      const prev = el.clawR.style.transform;
      el.clawR.animate([
        { transform: prev || 'rotate(-10deg)' },
        { transform: 'translate(0px,-18px) rotate(-52deg)' },
        { transform: 'translate(0px,-14px) rotate(-24deg)' },
        { transform: 'translate(0px,-18px) rotate(-52deg)' },
        { transform: prev || 'rotate(-10deg)' }],
      { duration: 1100, easing: 'ease-in-out' });
    } else if (bit === 'bubble') {
      for (let i = 0; i < 3; i += 1) {
        later(() => spawnFx(FX.bubbles, '🫧', [0.5, 0.66]), i * 260);
      }
    } else if (bit === 'hop') {
      motion.animate(ENTRANCES.hop, { duration: 560, easing: 'ease-out' });
    }
    return bit;
  }
  function scheduleIdle() {
    later(() => {
      if (IDLE_SAFE.has(mood) && !doc.hidden) playIdleBit();
      scheduleIdle();
    }, rand(8000, 15000));
  }

  // ── gear ───────────────────────────────────────────────────────────────
  // Live gear nodes by piece name. Gear sits in layers applyMood never
  // touches (and gloves ride the claw pose groups, so pose transitions and
  // the front/back re-ordering carry them along for free).
  const gearNodes = { helmet: null, gloves: null, pack: null, scarf: null, popcorn: null };

  function animateGearNode(node, piece) {
    node.style.transformBox = 'fill-box';
    node.style.transformOrigin = 'center';
    const a = GEAR_ANIM[piece];
    if (a) node.animate(a.frames, { duration: a.ms, easing: 'ease-out' });
  }

  function setGear(piece, on = true, { animate = true } = {}) {
    if (destroyed || !(piece in gearNodes)) return;
    if (!on) {
      const cur = gearNodes[piece];
      if (cur) (Array.isArray(cur) ? cur : [cur]).forEach((n) => n.remove());
      gearNodes[piece] = null;
      return;
    }
    if (gearNodes[piece]) return; // already wearing it
    if (piece === 'gloves') {
      el.clawL.insertAdjacentHTML('beforeend', GEAR.glove);
      el.clawR.insertAdjacentHTML('beforeend', GEAR.glove);
      const pair = [el.clawL.lastElementChild, el.clawR.lastElementChild];
      gearNodes.gloves = pair;
      if (animate) pair.forEach((n) => animateGearNode(n, 'gloves'));
    } else if (piece === 'pack') {
      el.sticker.insertAdjacentHTML('afterbegin', GEAR.pack);
      gearNodes.pack = el.sticker.firstElementChild;
      if (animate) animateGearNode(gearNodes.pack, 'pack');
    } else if (piece === 'helmet') {
      el.bodyTilt.insertAdjacentHTML('beforeend', GEAR.helmet);
      gearNodes.helmet = el.bodyTilt.lastElementChild;
      if (animate) {
        animateGearNode(gearNodes.helmet, 'helmet');
        // The clunk — body squashes right as the helmet lands.
        later(() => motion.animate([
          { transform: 'scaleY(1)' },
          { transform: 'scaleY(0.93)', offset: 0.5 },
          { transform: 'scaleY(1)' }],
        { duration: 260, easing: 'ease-out' }), 280);
      }
    } else if (piece === 'scarf' || piece === 'popcorn') {
      // watch-along garnish — rides bodyTilt like the helmet so it moves
      // with the character; no clunk, just the drop/pop entrance.
      el.bodyTilt.insertAdjacentHTML('beforeend', GEAR[piece]);
      gearNodes[piece] = el.bodyTilt.lastElementChild;
      if (animate) animateGearNode(gearNodes[piece], piece);
    }
  }

  function clearGear() {
    Object.keys(gearNodes).forEach((p) => setGear(p, false));
  }

  // One-shot celebration burst — confetti + stars fountain over ~1s.
  // Deliberately over the ambient-FX cap (its own config) so it reads as an
  // EVENT: liftoff, grade reveal, vote winner.
  function celebrate() {
    const burst = { glyphs: ['🎉', '✨', '⭐', '🎊', '🌟'], anim: 'chibiFxTwinkle', max: 26, size: [12, 20] };
    for (let i = 0; i < 18; i += 1) later(() => spawnFx(burst), i * 45);
  }

  // ── mood application ───────────────────────────────────────────────────
  function applyMood(name, { instant = false } = {}) {
    const cfg = MOODS[name] || MOODS.content;
    const eyeFn = EYES[cfg.eyes] || EYES.roundSparkle;
    el.eyeL.innerHTML = eyeFn(1);
    el.eyeR.innerHTML = eyeFn(-1);
    el.mouth.innerHTML = MOUTHS[cfg.mouth] || MOUTHS.kat3;
    el.prop.innerHTML = cfg.prop ? (PROPS[cfg.prop] || '') : '';
    el.prop.querySelectorAll('[data-prop-bob="1"]').forEach((g) => {
      g.style.transformBox = 'fill-box';
      g.style.transformOrigin = 'center';
      g.style.animation = 'chibiPropBob 2.2s ease-in-out infinite';
    });
    const blush = BLUSH_OPACITY[cfg.blush || 2];
    el.blushL.style.opacity = blush;
    el.blushR.style.opacity = blush;
    const pose = CLAW_POSES[cfg.claws] || CLAW_POSES.up;
    el.clawL.style.transform = pose.l;
    el.clawR.style.transform = pose.r;
    // Resting poses tuck the pincers behind the shell (PNG-faithful);
    // front poses bring them over the belly/face so they stay visible.
    if (FRONT_CLAW_POSES.has(cfg.claws)) {
      el.sticker.appendChild(el.clawOuterL);
      el.sticker.appendChild(el.clawOuterR);
    } else {
      // Backmost slot — but never behind the rocket pack (gear), which
      // must stay the rearmost layer so the arms draw over its straps.
      const backAnchor = gearNodes.pack
        ? gearNodes.pack.nextSibling
        : el.sticker.firstElementChild;
      el.sticker.insertBefore(el.clawOuterL, backAnchor);
      el.sticker.insertBefore(el.clawOuterR, backAnchor);
    }
    el.bodyTilt.style.transform = cfg.tilt ? `rotate(${cfg.tilt}deg)` : '';
    breathe.classList.toggle('slow', cfg.breathe === 'slow');
    setLook((cfg.look || {}).x || 0, (cfg.look || {}).y || 0, instant ? 0 : 420);

    // Continuous body loop for the mood (sway/bounce/tremble/giggle).
    if (loopAnim) { loopAnim.cancel(); loopAnim = null; }
    const loop = cfg.loop && LOOPS[cfg.loop];
    if (loop) loopAnim = motion.animate(loop.frames, loop.opts);

    // Ambient particles.
    stopFx();
    if (cfg.fx && FX[cfg.fx]) scheduleFx(FX[cfg.fx]);

    // Entrance one-shot (skipped on the initial mount / instant swaps).
    if (entranceAnim) { entranceAnim.cancel(); entranceAnim = null; }
    if (!instant) {
      const entrance = cfg.entrance || 'pop';
      entranceAnim = motion.animate(ENTRANCES[entrance] || ENTRANCES.pop, {
        duration: ENTRANCE_MS[entrance] || 300,
        easing: entrance === 'hop' ? 'ease-out' : 'ease-in-out',
      });
    }
  }

  function setMood(name, { instant = false } = {}) {
    const next = MOODS[name] ? name : 'content';
    if (next === mood) return;
    const prev = mood;
    mood = next;
    applyMood(next, { instant });
    if (typeof opts.onMoodChange === 'function' && prev !== null) {
      opts.onMoodChange(next, prev);
    }
  }

  function destroy() {
    destroyed = true;
    timers.forEach(clearTimeout);
    timers.clear();
    if (loopAnim) loopAnim.cancel();
    if (entranceAnim) entranceAnim.cancel();
    root.remove();
  }

  setMood(opts.mood || 'content', { instant: true });
  scheduleBlink();
  scheduleLook();
  scheduleIdle();

  return { setMood, lookAt, triggerIdleBit: playIdleBit, setGear, clearGear, celebrate, destroy, el: root, get mood() { return mood; } };
}

export { ALL_MOODS, MOODS, MOOD_EMOJI };
